import { setLightPaint } from "./theme";
import maplibregl, { type Map as MlMap } from "maplibre-gl";
import { openMapPopup } from "./popup-room";
import { depthAdviceHtml } from "./depth-ui";

/** Bangkok canal/river gauge points (POPNIX, real measurements) — solid dots +
 *  flood-depth emoji markers where water is over the bank. */
const SRC = "bkk-gauges";
const LAYER = "bkk-gauges-layer";
const LABEL = "bkk-gauges-label";
let loaded = false;
let credit = "ข้อมูล: สำนักการระบายน้ำ กทม. ผ่าน POPNIX Flood";

interface Gauge { id: number; name: string; river: string; lat: number; lng: number; level: string; marginCm: number | null; overtopping: boolean; measuredAt: string | null; }

/** Flood depth over the bank → body-part icon (SPEC v0.7 §3: ONE shared scale,
 *  used by the map markers, result card, and the "จุดท่วมสูงสุด" list). Does not
 *  imply the depth is safe to walk/drive through. */
export function depthIcon(cm: number): string {
  if (cm < 20) return "🦶";
  if (cm < 50) return "🦵";
  if (cm < 100) return "🧍";
  if (cm < 150) return "🆘";
  return "☠️";
}
export function depthLabel(cm: number): string {
  if (cm < 20) return "ถึงข้อเท้า";
  if (cm < 50) return "ถึงเข่า";
  if (cm < 100) return "ถึงเอว";
  if (cm < 150) return "ถึงอก";
  return "มิดศีรษะ";
}

let stations: Gauge[] = [];
let stationsP: Promise<Gauge[]> | null = null;

const DEPTH_EMOJI = ["🦶", "🦵", "🧍", "🆘", "☠️"]; // same 5-level scale as the list
function depthLevel(cm: number): number { return cm < 20 ? 0 : cm < 50 ? 1 : cm < 100 ? 2 : cm < 150 ? 3 : 4; }

/** Register emoji depth icons as map images (SDF fonts can't render colour emoji,
 *  so we rasterise each to a canvas and addImage once). */
function addDepthEmojiImages(map: MlMap): void {
  const size = 44;
  DEPTH_EMOJI.forEach((emoji, i) => {
    const id = `d${i}`;
    if (map.hasImage(id)) return;
    const c = document.createElement("canvas"); c.width = c.height = size;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    ctx.font = `${Math.round(size * 0.78)}px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",serif`;
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText(emoji, size / 2, size / 2 + 2);
    try { map.addImage(id, ctx.getImageData(0, 0, size, size), { pixelRatio: 2 }); } catch { /* already added */ }
  });
}

/** Fetch the POPNIX stations once (cached), independent of the map layer, so the
 *  result card can look up a nearby real gauge even before the layer is shown. */
export async function loadGaugeData(): Promise<Gauge[]> {
  if (!stationsP) {
    stationsP = fetch("/api/bkk/gauges")
      .then((r) => (r.ok ? (r.json() as Promise<{ source?: string; stations?: Gauge[] }>) : { stations: [] }))
      .then((d) => { if (d.source) credit = d.source; stations = d.stations ?? []; return stations; })
      .catch(() => (stations = []));
  }
  return stationsP;
}

/** Nearest real POPNIX over-bank gauge within `radiusKm`. Prefers the nearest
 *  FRESH reading (≤2h); only falls back to a stale one (flagged) if none fresh.
 *  Returns the over-bank depth in cm + freshness, or null. Bangkok only. */
export function nearestFloodGauge(lat: number, lon: number, radiusKm: number):
  { name: string; overtopCm: number; measuredAt: string | null; distKm: number; fresh: boolean } | null {
  const now = Date.now(), freshMs = POPNIX_FRESH_HOURS * 3600 * 1000;
  type G = { name: string; overtopCm: number; measuredAt: string | null; distKm: number; fresh: boolean };
  let bestFresh: G | null = null, bestAny: G | null = null;
  for (const s of stations) {
    if (!s.overtopping || s.marginCm == null) continue;
    const dKm = haversine(lat, lon, s.lat, s.lng);
    if (dKm > radiusKm) continue;
    const t = parseThTime(s.measuredAt);
    const fresh = !(t != null && now - t > freshMs);
    const g: G = { name: s.name, overtopCm: Math.abs(s.marginCm), measuredAt: s.measuredAt, distKm: dKm, fresh };
    if (!bestAny || dKm < bestAny.distKm) bestAny = g;
    if (fresh && (!bestFresh || dKm < bestFresh.distKm)) bestFresh = g;
  }
  return bestFresh ?? bestAny;
}

/** Freshness window for POPNIX ranking (SPEC v0.7 §3; provider updates ~2h). */
export const POPNIX_FRESH_HOURS = 2;

export interface FloodGauge { id: number; name: string; overtopCm: number; measuredAt: string | null; lat: number; lon: number; distKm: number | null; }

function parseThTime(s: string | null): number | null {
  if (!s) return null;
  const t = Date.parse(s.replace(" ", "T") + "+07:00");
  return Number.isFinite(t) ? t : null;
}

/** Over-bank gauges with depth > 0, split into fresh (rankable) and stale, each
 *  sorted deepest→shallowest, tie newer time first, then id (stable). Optional
 *  center adds a distance and (with radiusKm) a radius filter. */
export function floodGauges(center?: { lat: number; lon: number } | null, radiusKm?: number): { fresh: FloodGauge[]; stale: FloodGauge[] } {
  const now = Date.now(), freshMs = POPNIX_FRESH_HOURS * 3600 * 1000;
  const fresh: FloodGauge[] = [], stale: FloodGauge[] = [];
  for (const s of stations) {
    if (!s.overtopping || s.marginCm == null) continue;
    const cm = Math.abs(s.marginCm);
    if (cm <= 0) continue;
    const distKm = center ? haversine(center.lat, center.lon, s.lat, s.lng) : null;
    if (radiusKm != null && distKm != null && distKm > radiusKm) continue;
    const g: FloodGauge = { id: s.id, name: s.name, overtopCm: cm, measuredAt: s.measuredAt, lat: s.lat, lon: s.lng, distKm };
    const t = parseThTime(s.measuredAt);
    (t != null && now - t > freshMs ? stale : fresh).push(g);
  }
  const cmp = (a: FloodGauge, b: FloodGauge): number =>
    b.overtopCm - a.overtopCm || (parseThTime(b.measuredAt) ?? 0) - (parseThTime(a.measuredAt) ?? 0) || a.id - b.id;
  fresh.sort(cmp); stale.sort(cmp);
  return { fresh, stale };
}

function haversine(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const R = 6371, d2r = Math.PI / 180;
  const dLat = (bLat - aLat) * d2r, dLon = (bLon - aLon) * d2r;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(aLat * d2r) * Math.cos(bLat * d2r) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

function buildGaugeFC(center: { lat: number; lon: number } | null, radiusKm: number): GeoJSON.FeatureCollection {
  const now = Date.now(), freshMs = POPNIX_FRESH_HOURS * 3600 * 1000;
  return {
    type: "FeatureCollection",
    features: stations.map((s) => {
      const depthCm = s.overtopping && s.marginCm != null ? Math.abs(s.marginCm) : 0;
      const t = parseThTime(s.measuredAt);
      const fresh = !(t != null && now - t > freshMs); // same 2h rule as the Top-flood list
      const inRadius = !!center && depthCm > 0 && haversine(center.lat, center.lon, s.lat, s.lng) <= radiusKm;
      return {
        type: "Feature" as const,
        geometry: { type: "Point" as const, coordinates: [s.lng, s.lat] },
        properties: { name: s.name, marginCm: s.marginCm, overtopping: s.overtopping, measuredAt: s.measuredAt, depthCm, lvl: depthLevel(depthCm), fresh, inRadius },
      };
    }),
  };
}

/** Focus the gauges on a point+radius: pins inside show the big depth icon + cm at
 *  any zoom; others collapse to small dots. (v0.8.4 item 2) */
export async function setGaugeFocus(map: MlMap, center: { lat: number; lon: number } | null, radiusKm: number): Promise<void> {
  await loadGaugeData();
  const src = map.getSource(SRC) as maplibregl.GeoJSONSource | undefined;
  if (src) src.setData(buildGaugeFC(center, radiusKm));
}

async function ensure(map: MlMap): Promise<void> {
  if (loaded) return;
  await loadGaugeData();
  const fc = buildGaugeFC(null, 0);
  map.addSource(SRC, { type: "geojson", data: fc, attribution: credit });
  addDepthEmojiImages(map);
  // depth colour ramp: light blue → blue → purple → magenta → red
  const depthCol: maplibregl.ExpressionSpecification = ["match", ["get", "lvl"], 0, "#9fd3ff", 1, "#2f7bff", 2, "#7b3fe4", 3, "#c026d3", 4, "#ff3b5c", "#2f7bff"];
  // dot: bigger + depth-coloured when inside the radius; small grey otherwise; stale → grey.
  // v0.9.9a: a gauge with NO water stays quieter than the GISTDA flood extent in both modes —
  // dark: dim slate, thin stroke · light: pale grey, thin grey stroke (same depth colours when wet)
  const wet: maplibregl.ExpressionSpecification = [">", ["get", "depthCm"], 0];
  setLightPaint(LAYER, {
    "circle-radius": ["interpolate", ["linear"], ["zoom"],
      6, ["case", ["get", "inRadius"], ["case", wet, 6, 3], wet, 4, 1.6],
      13, ["case", ["get", "inRadius"], ["case", wet, 12, 5], wet, 5, 2.6]],
    "circle-color": ["case", ["!", ["get", "fresh"]], "#c9d0d9", wet, ["match", ["get", "lvl"], 0, "#4a90c8", 1, "#1f5fd6", 2, "#6a2fd1", 3, "#a51fb5", 4, "#d01c3e", "#1f5fd6"], "#cfd5dd"],
    "circle-stroke-color": ["case", wet, "#ffffff", "#9aa4b1"],
    "circle-stroke-width": ["case", wet, ["case", ["get", "inRadius"], 1.6, 0.6], 0.5],
    "circle-opacity": ["case", wet, ["case", ["get", "fresh"], 0.95, 0.5], 0.75],
  });
  map.addLayer({
    id: LAYER,
    type: "circle",
    source: SRC,
    layout: { visibility: "none" },
    paint: {
      "circle-radius": ["interpolate", ["linear"], ["zoom"],
        6, ["case", ["get", "inRadius"], ["case", wet, 6, 3], wet, 4, 1.8],
        13, ["case", ["get", "inRadius"], ["case", wet, 12, 5], wet, 5, 2.8]],
      "circle-color": ["case", ["!", ["get", "fresh"]], "#5d6878", wet, depthCol, "#556173"],
      "circle-stroke-color": ["case", wet, "#ffffff", "#8a96a6"],
      "circle-stroke-width": ["case", wet, ["case", ["get", "inRadius"], 1.6, 0.6], 0.4],
      "circle-opacity": ["case", wet, ["case", ["get", "fresh"], 0.95, 0.5], 0.5],
    },
  });
  // depth emoji + big "N ซม." — shown for flooded gauges INSIDE the radius, any zoom.
  // overlaps: deepest wins (symbol-sort-key = -depth). stale → grey + "(เก่า)".
  map.addLayer({
    id: LABEL,
    type: "symbol",
    source: SRC,
    layout: {
      visibility: "none",
      "icon-image": ["match", ["get", "lvl"], 0, "d0", 1, "d1", 2, "d2", 3, "d3", 4, "d4", "d2"],
      "icon-size": ["interpolate", ["linear"], ["zoom"], 8, 0.5, 13, 0.75], "icon-anchor": "bottom", "icon-offset": [0, -9], "icon-allow-overlap": true, "icon-optional": false,
      "text-field": ["concat", ["to-string", ["get", "depthCm"]], " ซม.", ["case", ["get", "fresh"], "", " (เก่า)"]],
      "text-font": ["Noto Sans Regular"],
      "text-size": ["interpolate", ["linear"], ["zoom"], 8, 12, 13, 15], "text-anchor": "top", "text-offset": [0, 0.35], "text-allow-overlap": false, "text-optional": true,
      "symbol-sort-key": ["-", 0, ["get", "depthCm"]],
    },
    paint: {
      "icon-opacity": ["case", ["get", "inRadius"], ["case", ["get", "fresh"], 1, 0.5], 0],
      "text-color": ["case", ["get", "fresh"], "#eaf4ff", "#b0bcca"],
      "text-halo-color": "#0a0f1a", "text-halo-width": 1.6,
      "text-opacity": ["case", ["get", "inRadius"], ["case", ["get", "fresh"], 1, 0.7], 0],
    },
    // only gauges inside the radius (the others were drawn at opacity 0 but still took collision
    // space, hiding river names on the map — v0.9.5c)
    filter: ["all", [">", ["get", "depthCm"], 0], ["get", "inRadius"]],
  });
  map.on("click", LAYER, (e) => {
    const f = e.features?.[0];
    if (!f) return;
    const p = f.properties as { name: string; marginCm: number | null; overtopping: boolean; measuredAt: string | null; depthCm: number; fresh: boolean };
    const margin = p.marginCm == null ? "ไม่มีข้อมูล" : p.overtopping ? `น้ำล้นตลิ่ง +${Math.abs(p.marginCm)} ซม. (${depthLabel(Math.abs(p.marginCm))})` : `ยังไม่ล้นตลิ่ง (0 ซม.)`;
    const zeroNote = p.depthCm > 0 ? "" : `<div class="muted small" style="margin-top:4px">จุดวัดนี้ 0 ซม. — ไม่ได้แปลว่าพื้นที่รอบไม่ท่วม</div>`;
    const staleNote = p.fresh ? "" : `<div class="muted small" style="margin-top:4px">⚠️ ข้อมูลเก่ากว่า ${POPNIX_FRESH_HOURS} ชม. — ไม่จัดอันดับ</div>`;
    const adviceNote = p.depthCm > 0 ? `<div class="pop-advice">${depthAdviceHtml(p.depthCm)}</div>` : ""; // D8: same measured-depth advice everywhere
    openMapPopup(map, e.lngLat,
      `<div class="pop"><div class="pop-row"><b>${esc(p.name)}</b></div><div class="pop-grid"><span>ระดับน้ำ</span><b>${margin}</b><span>เวลา</span><b>${p.measuredAt ?? "—"}</b></div>${adviceNote}${zeroNote}${staleNote}<div class="muted small" style="margin-top:6px">${esc(credit)}</div></div>`,
      "260px");
  });
  map.on("mouseenter", LAYER, () => { map.getCanvas().style.cursor = "pointer"; });
  map.on("mouseleave", LAYER, () => { map.getCanvas().style.cursor = ""; });
  loaded = true;
}

export async function setBkkGaugesVisible(map: MlMap, on: boolean): Promise<void> {
  if (on) await ensure(map);
  for (const id of [LAYER, LABEL]) if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", on ? "visible" : "none");
}

function esc(s: string): string { return s.replace(/[<>&"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" }[c] ?? c)); }
